import { getRepoId, installUrl, tryOpenUrl, pollForInstallation, probeAppInstallation } from "./gh-helpers.js";
import { signAppJwt } from "@m8t-stack/github-app-auth";

/**
 * Resolve the GitHub App installation id for a repo.
 *
 * 1. Probes once — fires `onAlreadyInstalled(id)` and returns immediately if
 *    the App is already installed.
 * 2. Otherwise: builds the install URL, calls `write` with the URL line,
 *    optionally opens a browser, then polls until the installation is detected
 *    (or times out).
 *
 * Shared by `m8t brain link` and `m8t coder deploy --brain`.
 */
export async function resolveAppInstallation(args: {
  owner: string;
  repo: string;
  slug: string;
  appId: string;
  privateKeyPem: string;
  /** Set to false to skip the tryOpenUrl call (e.g. in non-interactive / CI mode). */
  openBrowser?: boolean;
  /** Sink for user-facing messages (install URL line). */
  write: (s: string) => void;
  /** Called when the pre-probe finds an existing installation. Optional. */
  onAlreadyInstalled?: (installationId: string) => void;
  /** Called each poll tick. Optional. */
  onTick?: (attempt: number) => void;
}): Promise<string> {
  // Pre-probe — one-shot, safe to use a single JWT.
  const jwt = signAppJwt({ appId: args.appId, privateKeyPem: args.privateKeyPem });
  const existing = await probeAppInstallation({ owner: args.owner, repo: args.repo, jwt });
  if (existing) {
    args.onAlreadyInstalled?.(existing);
    return existing;
  }

  const repoId = await getRepoId(args.owner, args.repo);
  const url = installUrl(args.slug, repoId);
  args.write(`Install the m8t-brain App on ${args.owner}/${args.repo}:\n  ${url}\n`);
  if (args.openBrowser !== false) await tryOpenUrl(url);

  return pollForInstallation({
    owner: args.owner,
    repo: args.repo,
    // Mint a fresh JWT on every tick — App JWTs are 10-min TTL, poll can run up to 5 min.
    probe: () =>
      probeAppInstallation({
        owner: args.owner,
        repo: args.repo,
        jwt: signAppJwt({ appId: args.appId, privateKeyPem: args.privateKeyPem }),
      }),
    onTick: args.onTick,
  });
}
